import BenchlingConfig from 'BenchlingConfig';
import InventoryStructuredTableMode from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {
  TransferStructuredTable,
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {applyRowByRowSubmissionSettingsUpdate} from 'notebook/structured-tables/action-functions/updateStructuredTableSettingsActionFunction';
import StructuredTableSettingsModal, {
  getStructuredTableSettingsModalTitle,
} from 'notebook/structured-tables/components/modals/StructuredTableSettingsModal';
import {StructuredTableFolderOption} from 'notebook/structured-tables/components/useSelectFolderOptionForTable';
import updateStructuredTableMetadata from 'notebook/structured-tables/util/updateStructuredTableMetadata';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import runModal from 'ui-library/runModal';

import {ToolbarButtonActionFunction} from '../StructuredTableConstants';
import makeSimpleActionFunction from './makeSimpleActionFunction';

/**
 * An ActionFunction that lets the user view or update the table settings. Can only be changed if the editor
 * is unlocked and the table hasn't been submitted.
 *
 * Currently only has settings relevant to create tables & is hidden otherwise.
 */
export const updateTransferTableSettingsActionFunction: ToolbarButtonActionFunction<
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata
> = makeSimpleActionFunction({
  label: 'Settings',
  icon: 'settings',
  onInvoke: async ({
    structuredTable,
    locked,
    performTableOpOnTable,
    entryOrTemplateId,
    structuredTableInsertionContext,
  }) => {
    const transferTableSettingsValue = await runModal(
      StructuredTableSettingsModal,
      {
        folderSettings: {
          initialFolderConfig: {
            folderOption:
              structuredTable.metadata.folderOption ??
              StructuredTableFolderOption.DERIVE_FROM_PARENT_DOCUMENT_TYPE,
            folderId: structuredTable.metadata.folderId,
          },
          itemTypeSupportsFolders: false,
          isForCreate: structuredTable.metadata.mode === InventoryStructuredTableMode.CREATE,
          projectIsRequired: BenchlingConfig.ENABLE_PROJECTS_REQUIRED_FOR_NON_LOCATION_STORABLES,
          structuredTableInsertionContext,
        },
        locked: locked || structuredTable.rows.some((row) => row.isFinalized),
        rowOnlyConfig: {
          showSubmissionSetting:
            BenchlingConfig.ELN_ENABLE_ROW_ONLY_TABLE_SUBMISSION && structuredTable.isConfig,
          rowOnlyInitialValue: structuredTable.metadata.rowByRowSubmissionRequired ?? false,
        },
      },
      {title: getStructuredTableSettingsModalTitle(structuredTable), zIndex: ZINDEX_MODAL}
    );
    if (transferTableSettingsValue == null) {
      return;
    }

    const {folderConfig: newFolderConfig, rowByRowSubmissionRequired: newRowByRowSubmissionRequired} =
      transferTableSettingsValue;
    if (newFolderConfig != null) {
      updateStructuredTableMetadata<TransferStructuredTableMetadata>({
        performTableOpOnTable,
        metadata: newFolderConfig,
      });
    }
    applyRowByRowSubmissionSettingsUpdate({
      performTableOpOnTable,
      values: {rowByRowSubmissionRequired: newRowByRowSubmissionRequired},
      analyticsParams: {structuredTable, entryOrTemplateId},
    });
  },
  getTooltip: () => 'Settings',
  getDisabled: ({isProtected}) => Boolean(isProtected),
  getHidden: ({structuredTable}) =>
    (structuredTable as TransferStructuredTable).metadata.mode !== InventoryStructuredTableMode.CREATE,
});
